import { useEffect, useState } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import type { App } from '../lib/api'

interface AppDeleteDialogProps {
  app: App
  open: boolean
  onOpenChange: (open: boolean) => void
  onDelete: () => Promise<void>
}

export default function AppDeleteDialog({
  app,
  open,
  onOpenChange,
  onDelete,
}: AppDeleteDialogProps) {
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    if (!open) {
      setDeleteLoading(false)
    }
  }, [open])

  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (deleteLoading && !nextOpen) return
        onOpenChange(nextOpen)
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <AlertDialog.Content
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-xl p-6 w-full max-w-md z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <AlertDialog.Title className="text-lg font-semibold">
            Delete App
          </AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-muted-foreground mt-2">
            Delete &ldquo;{app.display_name || app.name}&rdquo;? You can restore it for 30 days.
          </AlertDialog.Description>
          <div className="flex justify-end gap-3 mt-6">
            <AlertDialog.Cancel asChild>
              <button
                disabled={deleteLoading}
                className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-secondary transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </AlertDialog.Cancel>
            <button
              disabled={deleteLoading}
              onClick={async () => {
                setDeleteLoading(true)
                try {
                  await onDelete()
                  onOpenChange(false)
                } catch {
                  // Keep dialog open on error
                } finally {
                  setDeleteLoading(false)
                }
              }}
              className="px-4 py-2 text-sm rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
